/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
/*
    xng-change: 
    * Update the structure to use the StyledWrapper instead of the Grid
    * Add the Part # label
    * Change the link to be on the product name only (FRD Requeriment)
    * Add the needed styles to autocompleteProductsStyles to match the mock-ups
    * Pass the doSearch function to update the Recommended Products to be link 
*/
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, ProductAutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { ellipsis, mergeWithCss } from "@monsma/XDL/Utilities";
import React from "react";
import { css } from "styled-components";

interface Props {
    products: ProductAutocompleteItemModel[];
    focusedItem?: AutocompleteItemModel;
    goToUrl: (url: string) => void;
    doSearch: () => void;
    extendedStyles?: AutocompleteProductsStyles;
}

export interface AutocompleteProductsStyles {
    headerLink?: LinkPresentationProps;
    wrapper?: InjectableCss;
    focusedWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    image?: LazyImageProps;
    infoWrapper?: InjectableCss;
    titleLink?: LinkPresentationProps;
    erpNumberWrapper?: InjectableCss;
    erpNumberlabel?: TypographyPresentationProps;
    erpNumberText?: TypographyPresentationProps;
}

export const autocompleteProductsStyles: AutocompleteProductsStyles = {
    headerLink: {
        typographyProps: {
            size: 14,
            lineHeight: "20px",
            weight: "bold",
        },
        color: "common.ui10",
        hoverStyle: {
            textDecoration: "underline",
        },
        css: css`
            margin-bottom: 15px;
            text-align: left;
        `,
    },
    wrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            text-align: left;
            padding-bottom: 30px;
            &:last-child {
                padding-bottom: 19px;
            }
        `,
    },
    imageWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            flex: 0 0 70px;
            height: 70px;
            margin-right: 20px;
            border: 1px solid ${getColor("common.ui12")};
            border-radius: 4px;
            overflow: hidden;
        `,
    },
    image: {
        css: css`
            img {
                height: auto;
                width: auto;
                max-height: 100%;
                max-width: 100%;
            }
        `,
    },
    erpNumberWrapper: {
        css: css`
            margin-bottom: 5px;
        `,
    },
    erpNumberlabel: {
        size: 13,
        lineHeight: "22px",
        css: css`
            margin-right: 5px;
        `,
    },
    erpNumberText: {
        size: 13,
        lineHeight: "22px",
        weight: 600,
    },
    titleLink: {
        typographyProps: {
            size: 14,
            lineHeight: "19px",
            weight: "bold",
            css: css`
                ${ellipsis(2, 19)}
            `,
        },
    },
};

autocompleteProductsStyles.focusedWrapper = mergeWithCss(autocompleteProductsStyles.wrapper, {
    css: css`
        background-color: ${getColor("common.ui7")};
    `,
});

const styles = autocompleteProductsStyles;

class AutocompleteProducts extends React.Component<Props> {
    private readonly styles: AutocompleteProductsStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }

    render() {
        const { products } = this.props;
        const styles = this.styles;
        return (
            <>
                <Link
                    {...styles.headerLink}
                    onClick={() => {
                        this.props.doSearch();
                    }}
                >
                    {translate("Recommended Products")}
                </Link>
                {products.map(product => (
                    <StyledWrapper
                        {...(this.props.focusedItem === product ? styles.focusedWrapper : styles.wrapper)}
                        key={product.id!}
                    >
                        <StyledWrapper {...styles.imageWrapper} className="autocomplete-products-image-grid-item">
                            <LazyImage {...styles.image} src={product.image} altText={product.displayTitle as any} className="autocomplete-products-image" />
                        </StyledWrapper>
                        <StyledWrapper {...styles.infoWrapper}  className="autocomplete-products-info-grid-item">
                            <StyledWrapper>
                                <Typography {...styles.erpNumberlabel}>{translate("Part #:")}</Typography>
                                <Typography {...styles.erpNumberText}>{product.displayErpNumber}</Typography>
                            </StyledWrapper>
                            <Link
                                {...styles.titleLink}
                                onClick={() => {
                                    this.props.goToUrl(product.url);
                                }}
                                data-test-selector={`productId_${product.id}`}
                                className="autocomplete-products-link"
                            >
                                {product.displayTitle}
                            </Link>
                            <Typography 
                                {...styles.erpNumberText} 
                                data-test-selector="autocompleteProducts_PartNumber" 
                                className="autocomplete-products-erp-number"
                            >
                                {product.isNameCustomerOverride ? (
                                    <>
                                        {translate("My Part #")}
                                        {product.displayName}
                                    </>
                                ) : (
                                    <>
                                        {translate("Part #")}
                                        {product.displayErpNumber}
                                    </>
                                )}
                                {product.manufacturerItemNumber && (
                                    <>
                                        &nbsp;
                                        {translate("MFG #")}
                                        {product.displayManufacturerItemNumber}
                                    </>
                                )}
                            </Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                ))}
            </>
        );
    }
}

export default AutocompleteProducts;
